import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, ChevronDown, X, Grid3X3, LayoutGrid, Filter,
  SlidersHorizontal, Home, Shirt, Smartphone, Sparkles, Watch, ShoppingBag, Crown,
  Dumbbell, Tent, Trophy, HeartPulse
} from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons mapping
const categoryIcons = {
  casa: Home,
  moda: Shirt,
  elettronica: Smartphone,
  bellezza: Sparkles,
  gioielli: Watch,
  fitness: Dumbbell,
  outdoor: Tent,
  sport: Trophy,
  salute: HeartPulse,
  default: ShoppingBag
};

const ProductsPage = () => {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [gridCols, setGridCols] = useState(4);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Sync state with URL params when they change (e.g., from menu navigation)
  useEffect(() => {
    const urlCategory = searchParams.get('category') || 'all';
    const urlSearch = searchParams.get('search') || '';
    const urlSort = searchParams.get('sort') || 'newest';
    
    if (urlCategory !== category) setCategory(urlCategory);
    if (urlSearch !== search) setSearch(urlSearch);
    if (urlSort !== sortBy) setSortBy(urlSort);
  }, [searchParams]);

  // Reset products when filters change
  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setLoading(true);
    fetchProducts(1, true);
    
    // Scroll to top when category changes
    window.scrollTo(0, 0);
  }, [search, category, sortBy, language]);

  // Infinite scroll observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0
    };

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
        loadMoreProducts();
      }
    }, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, page]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      const catsObj = response.data.categories;
      const catsArray = Object.entries(catsObj).map(([key, value]) => ({
        key,
        name: value.name,
        icon: categoryIcons[key] || categoryIcons.default
      }));
      // Add LUSSO as special category at the beginning
      const lussoCategory = { key: 'lusso', name: '✨ Lusso', icon: Crown };
      setCategories([lussoCategory, ...catsArray]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async (pageNum = 1, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '24',
        lang: language
      });
      if (search) params.append('search', search);
      if (category && category !== 'all') params.append('category', category);
      if (sortBy) params.append('sort', sortBy);

      const response = await axios.get(`${API}/products?${params}`);
      const newProducts = response.data.products;
      
      if (reset) {
        setProducts(newProducts);
      } else {
        setProducts(prev => [...prev, ...newProducts]);
      }
      
      setTotalPages(response.data.pages);
      setTotalProducts(response.data.total);
      setHasMore(pageNum < response.data.pages);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreProducts = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage, false);
  }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('all');
    setSortBy('newest');
  };

  const hasActiveFilters = search || category !== 'all' || sortBy !== 'newest';

  const getCategoryName = () => {
    if (category === 'all') return 'Tutti i Prodotti';
    const cat = categories.find(c => c.key === category);
    return cat?.name || 'Collezione';
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="products-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-2 block">
                Collezione
              </span>
              <h1 
                className="text-3xl md:text-5xl font-light tracking-tight"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {getCategoryName()}
              </h1>
              <p className="text-neutral-500 mt-3 text-sm font-light">
                {totalProducts.toLocaleString()} prodotti selezionati
              </p>
            </motion.div>
            
            {/* Search */}
            <motion.form 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              onSubmit={handleSearch} 
              className="relative w-full md:w-96"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Cerca nella collezione..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-neutral-50 border-0 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]/20 transition-all"
                data-testid="search-input"
              />
            </motion.form>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b border-neutral-100 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Mobile Filter Button */}
            <button
              onClick={() => setMobileFiltersOpen(true)}
              className="md:hidden flex items-center gap-2 px-4 py-2 bg-neutral-100 rounded-full text-sm"
            >
              <Filter className="h-4 w-4" />
              Filtri
            </button>
            
            {/* Category Pills - Desktop */}
            <div className="hidden md:flex items-center gap-3 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => setCategory('all')}
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.1em] whitespace-nowrap rounded-full transition-all duration-300 ${
                  category === 'all' 
                    ? 'bg-[#064E3B] text-white shadow-lg' 
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                Tutti
              </button>
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setCategory(cat.key)}
                    className={`flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.1em] whitespace-nowrap rounded-full transition-all duration-300 ${
                      category === cat.key 
                        ? 'bg-[#064E3B] text-white shadow-lg' 
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                    data-testid={`filter-${cat.key}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {cat.name}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-4">
              {/* Sort */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-neutral-100 text-sm text-neutral-600 pl-4 pr-10 py-2.5 rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#064E3B]/20"
                  data-testid="sort-select"
                >
                  <option value="newest">Novità</option>
                  <option value="price_asc">Prezzo ↑</option>
                  <option value="price_desc">Prezzo ↓</option>
                  <option value="popular">Popolari</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
              </div>

              {/* Grid Toggle - Desktop */}
              <div className="hidden md:flex items-center gap-1 border-l border-neutral-200 pl-4">
                <button
                  onClick={() => setGridCols(3)}
                  className={`p-2 rounded-lg transition-colors ${gridCols === 3 ? 'bg-neutral-100 text-black' : 'text-neutral-300 hover:text-neutral-500'}`}
                  data-testid="grid-3"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setGridCols(4)}
                  className={`p-2 rounded-lg transition-colors ${gridCols === 4 ? 'bg-neutral-100 text-black' : 'text-neutral-300 hover:text-neutral-500'}`}
                  data-testid="grid-4"
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-xs text-neutral-500 hover:text-[#064E3B] transition-colors"
                  data-testid="clear-filters"
                >
                  <X className="h-3 w-3" />
                  Cancella
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      <AnimatePresence>
        {mobileFiltersOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileFiltersOpen(false)}
              className="fixed inset-0 bg-black/50 z-50 md:hidden"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed right-0 top-0 bottom-0 w-80 bg-white z-50 md:hidden overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-lg font-medium">Filtri</h3>
                  <button onClick={() => setMobileFiltersOpen(false)}>
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-neutral-400 mb-4">Categoria</h4>
                    <div className="space-y-2">
                      <button
                        onClick={() => { setCategory('all'); setMobileFiltersOpen(false); }}
                        className={`w-full text-left px-4 py-3 rounded-lg text-sm ${category === 'all' ? 'bg-[#064E3B] text-white' : 'bg-neutral-100'}`}
                      >
                        Tutti i Prodotti
                      </button>
                      {categories.map((cat) => (
                        <button
                          key={cat.key}
                          onClick={() => { setCategory(cat.key); setMobileFiltersOpen(false); }}
                          className={`w-full text-left px-4 py-3 rounded-lg text-sm ${category === cat.key ? 'bg-[#064E3B] text-white' : 'bg-neutral-100'}`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10 md:py-16">
        {loading ? (
          <div className={`grid grid-cols-2 ${gridCols === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-4 md:gap-6`}>
            {[...Array(12)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-neutral-200 rounded-sm mb-4" />
                <div className="h-3 bg-neutral-200 rounded w-1/4 mb-2" />
                <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-neutral-200 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-neutral-100 flex items-center justify-center">
              <Search className="h-8 w-8 text-neutral-300" />
            </div>
            <p className="text-neutral-400 text-lg font-light mb-4">
              Nessun prodotto trovato
            </p>
            <button
              onClick={clearFilters}
              className="text-sm text-[#064E3B] hover:underline"
            >
              Rimuovi i filtri
            </button>
          </motion.div>
        ) : (
          <>
            <div className={`grid grid-cols-2 ${gridCols === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-4 md:gap-6`}>
              {products.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }}
                >
                  <ProductCard product={product} variant="luxury" />
                </motion.div>
              ))}
            </div>

            {/* Infinite Scroll Trigger */}
            <div ref={loadMoreRef} className="h-20 flex items-center justify-center mt-8">
              {loadingMore && (
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 border-2 border-[#064E3B] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-neutral-500">Caricamento...</span>
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <p className="text-sm text-neutral-400">
                  Hai visto tutti i {totalProducts} prodotti
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ProductsPage;
